import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../axiosInstance";

// ═══════════════════════════════════════════════════════════
// ─── Async Thunks ─────────────────────────────────────────
// ═══════════════════════════════════════════════════════════

/** GET /product - List products with query params (public) */
export const fetchProducts = createAsyncThunk(
  "products/fetchProducts",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/product`, { params });
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch products",
      );
    }
  },
);

/** GET /product/admin/all - Admin list (includes inactive) */
export const fetchAllProductsAdmin = createAsyncThunk(
  "products/fetchAllProductsAdmin",
  async ({ page = 1, limit = 20, search = "" } = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/product/admin/all`, {
        params: { page, limit, search },
      });
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch admin products",
      );
    }
  },
);

/** GET /product/slug/:slug - Fetch by slug (public) */
export const fetchProductBySlug = createAsyncThunk(
  "products/fetchProductBySlug",
  async (slug, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/product/slug/${slug}`);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch product",
      );
    }
  },
);

/** GET /product/:id - Fetch by ID (public) */
export const fetchProductById = createAsyncThunk(
  "products/fetchProductById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/product/${id}`);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch product",
      );
    }
  },
);

/** POST /product/create-product - Create product */
export const createProduct = createAsyncThunk(
  "products/createProduct",
  async (productData, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/product/create-product`, productData);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to create product",
      );
    }
  },
);

/** PUT /product/:id - Full update */
export const updateProduct = createAsyncThunk(
  "products/updateProduct",
  async ({ id, productData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/product/${id}`, productData);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update product",
      );
    }
  },
);

/** PATCH /product/:id - Partial update */
export const patchProduct = createAsyncThunk(
  "products/patchProduct",
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/product/${id}`, updates);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to patch product",
      );
    }
  },
);

/** DELETE /product/:id - Delete product */
export const deleteProduct = createAsyncThunk(
  "products/deleteProduct",
  async (id, { rejectWithValue }) => {
    try {
      // NOTE: original code had a malformed template literal here that
      // concatenated the config object into the URL string instead of
      // passing it as a second argument — fixed below.
      await api.delete(`/product/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to delete product",
      );
    }
  },
);

/** PATCH /product/:id/variants/:colorId/stock - Update stock */
export const updateStock = createAsyncThunk(
  "products/updateStock",
  async ({ productId, colorId, size, stock }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(
        `/product/${productId}/variants/${colorId}/stock`,
        { size, stock },
      );
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update stock",
      );
    }
  },
);

/** POST /product/:id/variants - Add variant */
export const addVariant = createAsyncThunk(
  "products/addVariant",
  async ({ productId, variant }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/product/${productId}/variants`,
        variant,
      );
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to add variant",
      );
    }
  },
);

/** DELETE /product/:id/variants/:colorId - Remove variant */
export const removeVariant = createAsyncThunk(
  "products/removeVariant",
  async ({ productId, colorId }, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(
        `/product/${productId}/variants/${colorId}`,
      );
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to remove variant",
      );
    }
  },
);

/** GET /product/:id/inventory - Inventory summary */
export const fetchInventory = createAsyncThunk(
  "products/fetchInventory",
  async (productId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/product/${productId}/inventory`);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch inventory",
      );
    }
  },
);

// ═══════════════════════════════════════════════════════════
// ─── Slice ────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════

const initialState = {
  // Data
  products: [],
  currentProduct: null,
  inventory: null,

  // Pagination & meta
  pagination: {
    page: 1,
    pages: 1,
    total: 0,
    limit: 20,
  },

  // UI State
  loading: false,
  loadingList: false,
  loadingDetails: false,
  loadingAction: false, // for create/update/delete
  error: null,
  actionError: null,
  successMessage: null,
};

const productSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
      state.inventory = null;
    },
    clearError: (state) => {
      state.error = null;
      state.actionError = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setLimit: (state, action) => {
      state.pagination.limit = action.payload;
    },
    // Local optimistic stock update (optional)
    localUpdateStock: (state, action) => {
      const { colorId, size, stock } = action.payload;
      if (!state.currentProduct) return;

      const variant = state.currentProduct.variants.find(
        (v) => v.color._id === colorId,
      );
      if (variant) {
        const sizeObj = variant.sizes.find((s) => s.size === size);
        if (sizeObj) sizeObj.stock = stock;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ─── fetchProducts ──────────────────────────────
      .addCase(fetchProducts.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loadingList = false;
        state.products = action.payload.data;
        state.pagination = {
          page: action.payload.page,
          pages: action.payload.pages,
          total: action.payload.total,
          limit: state.pagination.limit,
        };
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      // ─── fetchAllProductsAdmin ──────────────────────
      .addCase(fetchAllProductsAdmin.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(fetchAllProductsAdmin.fulfilled, (state, action) => {
        state.loadingList = false;
        state.products = action.payload.data;
      })
      .addCase(fetchAllProductsAdmin.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      // ─── fetchProductBySlug / fetchProductById ──────
      .addCase(fetchProductBySlug.pending, (state) => {
        state.loadingDetails = true;
        state.error = null;
      })
      .addCase(fetchProductBySlug.fulfilled, (state, action) => {
        state.loadingDetails = false;
        state.currentProduct = action.payload.data;
      })
      .addCase(fetchProductBySlug.rejected, (state, action) => {
        state.loadingDetails = false;
        state.error = action.payload;
      })
      .addCase(fetchProductById.pending, (state) => {
        state.loadingDetails = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loadingDetails = false;
        state.currentProduct = action.payload.data;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loadingDetails = false;
        state.error = action.payload;
      })

      // ─── createProduct ──────────────────────────────
      .addCase(createProduct.pending, (state) => {
        state.loadingAction = true;
        state.actionError = null;
        state.successMessage = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.loadingAction = false;
        state.products.unshift(action.payload.data);
        state.currentProduct = action.payload.data;
        state.successMessage = "Product created successfully";
        state.pagination.total += 1;
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loadingAction = false;
        state.actionError = action.payload;
      })

      // ─── updateProduct ──────────────────────────────
      .addCase(updateProduct.pending, (state) => {
        state.loadingAction = true;
        state.actionError = null;
        state.successMessage = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.loadingAction = false;
        const updated = action.payload.data;
        const idx = state.products.findIndex((p) => p._id === updated._id);
        if (idx !== -1) state.products[idx] = updated;
        if (state.currentProduct?._id === updated._id) {
          state.currentProduct = updated;
        }
        state.successMessage = "Product updated successfully";
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.loadingAction = false;
        state.actionError = action.payload;
      })

      // ─── patchProduct ───────────────────────────────
      .addCase(patchProduct.pending, (state) => {
        state.loadingAction = true;
        state.actionError = null;
      })
      .addCase(patchProduct.fulfilled, (state, action) => {
        state.loadingAction = false;
        const updated = action.payload.data;
        const idx = state.products.findIndex((p) => p._id === updated._id);
        if (idx !== -1) state.products[idx] = updated;
        if (state.currentProduct?._id === updated._id) {
          state.currentProduct = updated;
        }
        state.successMessage = "Product updated successfully";
      })
      .addCase(patchProduct.rejected, (state, action) => {
        state.loadingAction = false;
        state.actionError = action.payload;
      })

      // ─── deleteProduct ──────────────────────────────
      .addCase(deleteProduct.pending, (state) => {
        state.loadingAction = true;
        state.actionError = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.loadingAction = false;
        state.products = state.products.filter((p) => p._id !== action.payload);
        if (state.currentProduct?._id === action.payload) {
          state.currentProduct = null;
        }
        state.pagination.total -= 1;
        state.successMessage = "Product deleted successfully";
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.loadingAction = false;
        state.actionError = action.payload;
      })

      // ─── updateStock ────────────────────────────────
      .addCase(updateStock.pending, (state) => {
        state.loadingAction = true;
      })
      .addCase(updateStock.fulfilled, (state, action) => {
        state.loadingAction = false;
        const updated = action.payload.data;
        const idx = state.products.findIndex((p) => p._id === updated._id);
        if (idx !== -1) state.products[idx] = updated;
        if (state.currentProduct?._id === updated._id) {
          state.currentProduct = updated;
        }
        state.successMessage = "Stock updated successfully";
      })
      .addCase(updateStock.rejected, (state, action) => {
        state.loadingAction = false;
        state.actionError = action.payload;
      })

      // ─── addVariant ─────────────────────────────────
      .addCase(addVariant.pending, (state) => {
        state.loadingAction = true;
      })
      .addCase(addVariant.fulfilled, (state, action) => {
        state.loadingAction = false;
        const updated = action.payload.data;
        const idx = state.products.findIndex((p) => p._id === updated._id);
        if (idx !== -1) state.products[idx] = updated;
        if (state.currentProduct?._id === updated._id) {
          state.currentProduct = updated;
        }
        state.successMessage = "Variant added successfully";
      })
      .addCase(addVariant.rejected, (state, action) => {
        state.loadingAction = false;
        state.actionError = action.payload;
      })

      // ─── removeVariant ──────────────────────────────
      .addCase(removeVariant.pending, (state) => {
        state.loadingAction = true;
      })
      .addCase(removeVariant.fulfilled, (state, action) => {
        state.loadingAction = false;
        const updated = action.payload.data;
        const idx = state.products.findIndex((p) => p._id === updated._id);
        if (idx !== -1) state.products[idx] = updated;
        if (state.currentProduct?._id === updated._id) {
          state.currentProduct = updated;
        }
        state.successMessage = "Variant removed successfully";
      })
      .addCase(removeVariant.rejected, (state, action) => {
        state.loadingAction = false;
        state.actionError = action.payload;
      })

      // ─── fetchInventory ─────────────────────────────
      .addCase(fetchInventory.pending, (state) => {
        state.loadingDetails = true;
      })
      .addCase(fetchInventory.fulfilled, (state, action) => {
        state.loadingDetails = false;
        state.inventory = action.payload.data;
      })
      .addCase(fetchInventory.rejected, (state, action) => {
        state.loadingDetails = false;
        state.error = action.payload;
      });
  },
});

// ═══════════════════════════════════════════════════════════
// ─── Actions & Selectors ──────────────────────────────────
// ═══════════════════════════════════════════════════════════

export const {
  clearCurrentProduct,
  clearError,
  clearSuccessMessage,
  setPage,
  setLimit,
  localUpdateStock,
} = productSlice.actions;

// Selectors
export const selectAllProducts = (state) => state.products.products;
export const selectCurrentProduct = (state) => state.products.currentProduct;
export const selectInventory = (state) => state.products.inventory;
export const selectProductLoading = (state) => state.products.loading;
export const selectProductListLoading = (state) => state.products.loadingList;
export const selectProductDetailsLoading = (state) =>
  state.products.loadingDetails;
export const selectProductActionLoading = (state) =>
  state.products.loadingAction;
export const selectProductError = (state) => state.products.error;
export const selectProductActionError = (state) => state.products.actionError;
export const selectProductSuccessMessage = (state) =>
  state.products.successMessage;
export const selectProductPagination = (state) => state.products.pagination;

// Derived: Get stock for a specific color + size
export const selectStockByColorAndSize = (colorId, size) => (state) => {
  const product = state.products.currentProduct;
  if (!product) return 0;
  const variant = product.variants.find((v) => v.color._id === colorId);
  if (!variant) return 0;
  const sizeObj = variant.sizes.find((s) => s.size === size);
  return sizeObj ? sizeObj.stock : 0;
};

// Derived: Check if a size is available for a color
export const selectIsSizeAvailable = (colorId, size) => (state) => {
  return selectStockByColorAndSize(colorId, size)(state) > 0;
};

// Derived: All colors of current product
export const selectCurrentProductColors = (state) => {
  return state.products.currentProduct?.availableColors || [];
};

// Derived: All sizes of current product
export const selectCurrentProductSizes = (state) => {
  return state.products.currentProduct?.availableSizes || [];
};

export default productSlice.reducer;
